import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ProductAddPage() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    stock: '',
    image: '',
    images: [''],
    weights: [{ weight: '', price: '' }],
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeImageField = (index) => {
    if (formData.images.length === 1) return;
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleWeightChange = (index, field, value) => {
    const newWeights = [...formData.weights];
    newWeights[index] = { ...newWeights[index], [field]: value };
    setFormData(prev => ({ ...prev, weights: newWeights }));
  };

  const addWeightField = () => {
    setFormData(prev => ({ ...prev, weights: [...prev.weights, { weight: '', price: '' }] }));
  };

  const removeWeightField = (index) => {
    if (formData.weights.length === 1) return;
    const newWeights = [...formData.weights];
    newWeights.splice(index, 1);
    setFormData(prev => ({ ...prev, weights: newWeights }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setSaving(true);

    if (!formData.name.trim()) {
      setError('Product name is required');
      setSaving(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          stock: Number(formData.stock),
          weights: formData.weights.map(w => ({
            weight: w.weight.trim(),
            price: Number(w.price),
          })),
          images: formData.images.filter(img => img.trim() !== ''),
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Failed to create product');
      }

      setSuccessMsg('Product added successfully!');
      setTimeout(() => navigate('/products'), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container my-5" style={{ maxWidth: '600px' }}>
      <h2 className="mb-4 text-center">Add New Product</h2>

      {successMsg && <div className="alert alert-success rounded-3 py-2 px-3">{successMsg}</div>}
      {error && <div className="alert alert-danger rounded-3 py-2 px-3">{error}</div>}

      <form onSubmit={handleSubmit} className="shadow rounded p-4 bg-white">
        {/* Name */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Name</label>
          <input
            name="name"
            type="text"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
            required
            autoFocus
            placeholder="Enter product name"
          />
        </div>

        {/* Category */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Category</label>
          <input
            name="category"
            type="text"
            className="form-control"
            value={formData.category}
            onChange={handleChange}
            placeholder="Enter category name"
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Description</label>
          <textarea
            name="description"
            className="form-control"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            placeholder="Write a brief description"
          />
        </div>

        {/* Stock */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Stock</label>
          <input
            name="stock"
            type="number"
            min="0"
            className="form-control"
            value={formData.stock}
            onChange={handleChange}
            placeholder="Available stock quantity"
          />
        </div>

        {/* Main Image */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Main Image URL</label>
          <input
            name="image"
            type="text"
            className="form-control"
            value={formData.image}
            onChange={handleChange}
            placeholder="Enter main image URL"
          />
        </div>

        {/* Additional Images */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Additional Image URLs</label>
          {formData.images.map((img, idx) => (
            <div key={idx} className="input-group mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Image URL"
                value={img}
                onChange={(e) => handleImageChange(idx, e.target.value)}
              />
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => removeImageField(idx)}
                disabled={formData.images.length === 1}
                title="Remove this image"
              >
                &times;
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={addImageField}
          >
            + Add Image
          </button>
        </div>

        {/* Weights and Prices */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Weights and Prices</label>
          {formData.weights.map((w, idx) => (
            <div key={idx} className="row mb-2 align-items-center">
              <div className="col-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Weight (e.g., 1kg)"
                  value={w.weight}
                  onChange={(e) => handleWeightChange(idx, 'weight', e.target.value)}
                />
              </div>
              <div className="col-5">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Price"
                  value={w.price}
                  onChange={(e) => handleWeightChange(idx, 'price', e.target.value)}
                  min="0"
                />
              </div>
              <div className="col-1 d-flex justify-content-center">
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => removeWeightField(idx)}
                  disabled={formData.weights.length === 1}
                  title="Remove this weight/price"
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={addWeightField}
          >
            + Add Weight/Price
          </button>
        </div>

        <button
          type="submit"
          className="btn btn-success w-100 fw-semibold"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Add Product'}
        </button>
      </form>
    </div>
  );
}

export default ProductAddPage;

